
import { useState, useCallback, useEffect } from "react";
import { ShoppingItem } from "@/types";
import { updateShoppingItem } from "@/services/shopping/items/index";
import { loadQuantityPresets } from "@/services/shopping/units.service";
import { deleteShoppingItem } from "@/services/shopping/items/index";
import { ProductAttribute } from "@/types/attributes";
import { getProductForShoppingItem } from "@/services/shopping/items/index";
import { updateUserProduct } from "@/services/products.service";
import { useQueryClient } from "@tanstack/react-query";
import { useUndoToast } from "@/hooks/useUndoToast";
import { shoppingItemRepository } from "@/repositories/shoppingItem.repository";
import { syncService } from "@/services/sync.service";
import logger from "@/utils/logger";

interface UseItemFormProps {
  item: ShoppingItem;
  onOpenChange: (isOpen: boolean) => void;
  onItemUpdate: (item: ShoppingItem | null) => void;
  toast: any;
  /**
   * ID della lista a cui appartiene l'articolo.
   * Serve per invalidare correttamente la query delle shopping-items
   * dopo l'eliminazione.
   */
  listId?: string;
}

export const useItemForm = ({ item, onOpenChange, onItemUpdate, toast, listId }: UseItemFormProps) => {
  const [quantity, setQuantity] = useState(item.quantity);
  const [unit, setUnit] = useState(item.unit);
  const [category, setCategory] = useState<string>(item.category);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [quantityPresets, setQuantityPresets] = useState<string[]>([]);
  const [product, setProduct] = useState<any>(null);
  const [productAttributes, setProductAttributes] = useState<ProductAttribute[]>([]);
  
  const [attributes, setAttributes] = useState<ProductAttribute[]>(
    Array.isArray(item.attributes) ? item.attributes : []
  );
  const queryClient = useQueryClient();
  const undoToast = useUndoToast();

  const mergeAttributesSafely = (existing: ProductAttribute[], incoming: ProductAttribute[]) => {
    const existingLabels = new Set(existing.map(attr => attr.label.trim().toLowerCase()));

    const newUniqueAttributes = incoming.filter(attr => {
      return !existingLabels.has(attr.label.trim().toLowerCase());
    });

    return [...existing, ...newUniqueAttributes];
  };

  useEffect(() => {
    const fetchPresets = async () => {
      const presets = await loadQuantityPresets(unit);
      setQuantityPresets(presets);
    };
  
    if (unit) {
      fetchPresets();
    }
  }, [unit]);
  
  // Load the product to get its attributes for suggestions
  useEffect(() => {
    const loadProduct = async () => {
      if (item.product_id) {
        const productData = await getProductForShoppingItem(item.product_id);
        if (productData) {
          setProduct(productData);
          // Set product attributes for suggestions
          if (Array.isArray(productData.attributes)) {
            // Filtra solo gli attributi NON già presenti nell'articolo
            const existingAttrs = Array.isArray(item.attributes) ? item.attributes : [];
          
            const filteredAttributes = productData.attributes.filter(attr =>
              !existingAttrs.some(a =>
                (a.id && attr.id && a.id === attr.id) ||
                (a.label && attr.label && a.label.toLowerCase() === attr.label.toLowerCase())
              )
            );
          
            setProductAttributes(filteredAttributes);
          }
        }
      }
    };
    
    loadProduct();
  }, [item.product_id]);
  
  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    
    setIsSubmitting(true);
    
    try {
      const updatedItem = {
        ...item,
        quantity,
        unit,
        category,
        attributes
      };
      
      const success = await updateShoppingItem(item.id, {
        quantity,
        unit,
        category,
        attributes
      });
      
      if (success) {
        // Synchronize new attributes with product attributes if they don't exist
        if (product && Array.isArray(attributes) && attributes.length > 0) {
          // Find attributes that are not in product.attributes
          const productAttrs = Array.isArray(product.attributes) ? product.attributes : [];

          const mergedAttributes = mergeAttributesSafely(productAttrs, attributes);

          if (mergedAttributes.length !== productAttrs.length) {
            await updateUserProduct(product.id, {
              attributes: mergedAttributes
            });
            queryClient.invalidateQueries({ queryKey: ["user-products"] });

            logger.log("Product attributes updated with new attributes from shopping item");
          }
        }
        
        onItemUpdate(updatedItem);
        onOpenChange(false);
        
        toast({
          description: "Articolo aggiornato",
          variant: "success"
        });
      } else {
        toast({
          title: "Errore",
          description: "Impossibile aggiornare l'articolo",
          variant: "destructive"
        });
      }
    } catch (error) {
      console.error("Error updating item:", error);
      toast({
        title: "Errore",
        description: "Si è verificato un errore",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  }, [item, quantity, unit, category, attributes, onItemUpdate, onOpenChange, toast, product]);

  const handleDeleteItem = useCallback(async () => {
    if (!item?.id) return;

    const deletedItem = { ...item };

    try {
      const success = await deleteShoppingItem(item.id);

      if (success) {
        undoToast(
          `delete-item-${item.id}`,
          "Articolo eliminato dalla lista",
          async () => {
            try {
              const key = ["shopping-items", listId || deletedItem.list_id] as const;
              queryClient.setQueryData<ShoppingItem[]>(key, (curr = []) =>
                curr.some(i => i.id === deletedItem.id) ? curr : [...curr, deletedItem]
              );
              await shoppingItemRepository.create(deletedItem, listId || deletedItem.list_id);
              await syncService.syncPendingOperations();
              await queryClient.invalidateQueries({ queryKey: key });
            } catch (err) {
              logger.error("Errore ripristinando l'articolo:", err);
              toast({
                title: "Errore",
                description: "Impossibile ripristinare l'articolo",
                variant: "destructive"
              });
            }
          }
        );

        onItemUpdate(null);
        onOpenChange(false);

        // Aggiorna i dati della lista invalidando la query relativa
        if (listId) {
          await queryClient.invalidateQueries({ queryKey: ["shopping-items", listId] });
        } else {
          await queryClient.invalidateQueries({ queryKey: ["shopping-items"] });
        }
      } else {
        throw new Error("Impossibile eliminare l'articolo");
      }
    } catch (error) {
      logger.error("Errore eliminando l'articolo:", error);
      toast({
        title: "Errore",
        description: "Impossibile eliminare l'articolo dalla lista",
        variant: "destructive"
      });
    }
  }, [item, onItemUpdate, onOpenChange, toast, listId, queryClient, undoToast]);

  const handleQuantitySelect = useCallback((value: string) => {
    setQuantity(value);
  }, []);

  const handleAddAttribute = useCallback((attribute: ProductAttribute) => {
    setAttributes(prev => [...prev, attribute]);
  }, []);
  
  const handleRemoveAttribute = useCallback((attributeId: string) => {
    setAttributes(prev => prev.filter(attr => attr.id !== attributeId));
  }, []);

  return {
    quantity,
    setQuantity,
    unit,
    setUnit,
    category,
    setCategory,
    attributes,
    setAttributes,
    isSubmitting,
    setIsSubmitting,
    quantityPresets,
    setQuantityPresets,
    productAttributes,
    handleSubmit,
    handleDeleteItem,
    handleQuantitySelect,
    handleAddAttribute,
    handleRemoveAttribute
  };
};
